import { useCallback, useRef, useReducer } from 'react';
import { AuthApi } from './api/api';
import { AuthUser, AuthContextType, IdentifyCurrentUserQueryResult } from './auth.types';
import { withWindow } from '@utils/withWindow';
import produce from 'immer';
import { LoginManager } from './loginManager';

type UseIdentifyEventUserArgs = {
  apiRef: React.MutableRefObject<AuthApi>;
  loginManager: LoginManager;
};

type UserIdentityState = {
  hasInitializedOnce: boolean;
  user: AuthUser;
};

type IdentityReducerActions =
  | ReducerAction<'HANDLE_MISSING_TOKEN'>
  | ReducerAction<'RESET_AUTH_USER'>
  | ReducerActionWithPayload<'IDENTIFY_USER', IdentifyCurrentUserQueryResult>;

const INITIAL_STATE: UserIdentityState = {
  hasInitializedOnce: false,
  user: {
    profile: null,
    activeEventUser: null,
    eventMemberships: [],
  },
};

// TODO: Consider storing this in local storage to consistently load event profile
const _deriveEventIdFromMemory = () => {
  return withWindow(global => {
    const { location } = global;
    const nextUrl = new URL(location.href);
    let eventIdFromUrl = nextUrl.searchParams.get('eventId');
    return typeof eventIdFromUrl === 'string' ? eventIdFromUrl : null;
  }, null);
};

const _useIdentityReducer = () => {
  return useReducer<(state: UserIdentityState, actions: IdentityReducerActions) => UserIdentityState>(
    produce((draft, action) => {
      switch (action.type) {
        case 'HANDLE_MISSING_TOKEN': {
          draft.hasInitializedOnce = true;
          draft.user = {
            profile: null,
            activeEventUser: null,
            eventMemberships: [],
          };
          break;
        }

        case 'IDENTIFY_USER': {
          const { data } = action.payload;
          draft.hasInitializedOnce = true;
          const userDraft = draft.user;

          if (data?.me) {
            const { activeEventUser = null, eventMemberships = [], ...profile } = data.me;
            userDraft.profile = profile;
            userDraft.activeEventUser = activeEventUser;
            userDraft.eventMemberships = eventMemberships;
          } else {
            // If data does not exist, clear out user/event user data.
            userDraft.activeEventUser = null;
            userDraft.eventMemberships = [];
            userDraft.profile = null;
          }
          break;
        }

        case 'RESET_AUTH_USER': {
          draft.hasInitializedOnce = draft.hasInitializedOnce || !!draft.user.profile;
          draft.user = {
            profile: null,
            activeEventUser: null,
            eventMemberships: [],
          };
          break;
        }
        default:
          break;
      }
    }),
    INITIAL_STATE
  );
};

export const useIdentity = ({ apiRef, loginManager }: UseIdentifyEventUserArgs) => {
  const [state, dispatch] = _useIdentityReducer();
  // Storing current event id via ref to minimize re-renders.
  const currentEventIdRef = useRef<string | null>(_deriveEventIdFromMemory());

  const setCurrentEventId = useCallback((id: string | null) => {
    currentEventIdRef.current = id;
  }, []);

  const tryToIdentifyUser = useCallback(async () => {
    let data: IdentifyCurrentUserQueryResult['data'];
    let errors: IdentifyCurrentUserQueryResult['errors'];
    try {
      const result = await apiRef.current.identifyCurrentUser(currentEventIdRef.current);
      data = result.data;
      errors = result.errors;
      if (!data?.me?.activeEventUser) {
        // No associated event user for current event id.
        setCurrentEventId(null);
      }
    } catch (e) {
      data = null;
      errors = [e as Error];
    }

    dispatch({ type: 'IDENTIFY_USER', payload: { data, errors } });
  }, [apiRef, dispatch, setCurrentEventId]);

  const processAccessToken = useCallback(
    async (token: string | null) => {
      if (token) {
        tryToIdentifyUser();
      } else {
        dispatch({ type: 'HANDLE_MISSING_TOKEN' });
      }
    },
    [dispatch, tryToIdentifyUser]
  );

  const checkAndProcessAccessToken = useCallback(() => {
    processAccessToken(loginManager.getToken());
  }, [loginManager, processAccessToken]);

  const switchToEvent = useCallback<AuthContextType['switchToEvent']>(
    id => {
      setCurrentEventId(id);
      tryToIdentifyUser();
    },
    [setCurrentEventId, tryToIdentifyUser]
  );

  const provideLogout = useCallback(() => {
    setCurrentEventId(null);
    loginManager.clearToken();
    dispatch({ type: 'RESET_AUTH_USER' });
  }, [dispatch, loginManager, setCurrentEventId]);

  return {
    ...state,
    processAccessToken,
    provideLogout,
    switchToEvent,
    checkAndProcessAccessToken,
  };
};
